/**
 * Read a storage object PAST the CDN.
 *
 * Supabase serves `storage/v1/object/<bucket>/<path>` through a CDN with
 * `cache-control: public, max-age=3600`, and its smart-CDN invalidation does
 * not reliably follow an upsert: on 2026-09-16 a chart's `.json` came back
 * `cf-cache-status: HIT` with a copy from twenty hours earlier — an empty
 * processedSources ledger — while the object itself had been rewritten three
 * times since. Every reload re-hydrated that stale ledger, every source went
 * back to "new", and the next autosave wrote the empty ledger over the real
 * one. Anything that decides behaviour from a document it just read (the
 * ledger, a reset-to-seed reload, a saved diff) has to read the live object.
 *
 * `supabase.storage.download()` cannot carry a query string, so this builds
 * the same request with a per-call cache-buster the CDN keys on, plus
 * `cache: 'no-store'` for the browser's own cache.
 */
async function fetchFresh(client, bucket, path) {
  const storage = client?.storage;
  if (!storage?.url || typeof storage.fetch !== "function") {
    throw new Error("downloadFresh: client has no storage transport");
  }
  const url = `${storage.url}/object/${bucket}/${encodeURI(path)}?fresh=${Date.now()}`;
  // The client's own fetch: it is what adds `apikey` and `Authorization`
  // (from the session, or the service key) — they are not in `headers`.
  const res = await storage.fetch(url, { headers: { ...(storage.headers || {}) }, cache: "no-store" });
  if (!res.ok) {
    throw new Error(`downloadFresh: ${res.status} for ${path}`);
  }
  return res;
}

/** The object as a Blob, like `storage.download()` but never from the CDN cache. */
export async function downloadFresh(client, bucket, path) {
  return (await fetchFresh(client, bucket, path)).blob();
}

/** The object parsed as JSON. */
export async function downloadFreshJson(client, bucket, path) {
  return (await fetchFresh(client, bucket, path)).json();
}
